import React, { useEffect, useMemo, useRef } from "react";

import { Canvas, useThree } from "@react-three/fiber";

import { OrbitControls } from "@react-three/drei";

import { MOUSE } from "three";

import TAMAtlas from "./tam/TAMAtlas";

import { disposeTAM, generateTAM } from "./tam/TAMGenerator";

import SurfaceControls from "./ui/SurfaceControls";

import { getTextureDefinition, TEXTURE_LIST } from "./materials/TextureLibrary";

import RenderObject from "./scene/RenderObject";

import RenderInfo from "./ui/RenderInfo";

import { getModel } from "./scene/ModelLibrary";

import TechniqueSelector from "./ui/TechniqueSelector";

import TechniqueRenderer from "./techniques/TechniqueRenderer";

import ModelSelector from "./ui/ModelSelector";

import LightingControls from "./ui/LightingControls";

import SceneControls from "./ui/SceneControls";

import TechniqueControls from "./techniques/TechniqueControls";

import useRenderingState from "./hooks/useRenderingState";

import "./RenderingDemo.css";

const RENDERING_INFO = {
  hatching: {
    eyebrow: "NON-PHOTOREALISTIC RENDERING · HATCHING",
    title: "Tonal Art Map Hatching",

    description:
      "A real-time non-photorealistic hatching renderer based on Tonal Art Maps. Illumination selects and blends between progressively denser hatch textures while curvature-derived surface directions orient the strokes across the model.",

    implementation: [
      "Procedurally generates a hierarchy of Tonal Art Maps with increasing stroke density",
      "Blends between six tonal textures continuously according to per-fragment illumination",
      "Uses object-space triplanar projection to avoid dependence on model UV parameterization",
      "Orients each triplanar projection using a curvature-derived tangent direction stored per vertex",
      "Maintains hatch patterns in object space so strokes remain attached to the surface during camera movement",
      "Includes an interactive atlas view for inspecting the generated tone and scale hierarchy",
      {
        text: "Based on the Tonal Art Map technique introduced in Real-Time Hatching",
        links: [
          {
            term: "Real-Time Hatching",
            url: "https://hhoppe.com/proj/hatching/",
          },
        ],
      },
    ],

    pipeline: [
      "Surface Curvature",
      "TAM Generation",
      "Illumination",
      "Tone Blending",
      "Oriented Hatching",
    ],

    references: [
      {
        title: "Real-Time Hatching",
        meta: "Praun, Hoppe, Webb & Finkelstein · SIGGRAPH 2001",
        url: "https://hhoppe.com/proj/hatching/",
      },
    ],
  },

  toon: {
    eyebrow: "NON-PHOTOREALISTIC RENDERING · TOON",
    title: "Toon Shading",

    description:
      "A discrete lighting shader that converts continuous diffuse illumination into a configurable number of tonal bands, producing the stepped shading characteristic of cel and toon rendering.",

    implementation: [
      "Computes conventional diffuse illumination from the surface normal and directional light",
      "Combines diffuse lighting with ambient occlusion before quantization",
      "Quantizes continuous illumination into a configurable number of discrete bands",
      "Preserves surface textures, normal mapping, ambient occlusion, and emissive contributions",
      "Performs the complete stylization per fragment in a custom GLSL shader",
    ],

    pipeline: [
      "Surface Normal",
      "Diffuse Lighting",
      "Illumination",
      "Band Quantization",
      "Toon Shading",
    ],
  },

  gooch: {
    eyebrow: "NON-PHOTOREALISTIC RENDERING · TECHNICAL ILLUSTRATION",
    title: "Gooch Shading",

    description:
      "A non-photorealistic lighting model for technical illustration that communicates surface orientation through hue rather than relying entirely on changes in luminance. Surfaces transition from cool tones facing away from the light to warm tones facing toward it.",

    implementation: [
      "Uses the full signed N·L range rather than clamping back-facing surfaces to zero",
      "Remaps surface orientation from the cool endpoint to the warm endpoint",
      "Allows configurable cool and warm colors",
      "Mixes the underlying surface color independently into both tonal endpoints",
      "Retains ambient occlusion and emissive surface contributions",
      {
        text: "Implements the cool-to-warm technical-illustration lighting model introduced by Gooch et al.",
        links: [
          {
            term: "Gooch et al.",
            url: "https://www-old.cs.utah.edu/gdc/publications/papers/gooch98.pdf",
          },
        ],
      },
    ],

    pipeline: [
      "Surface Normal",
      "Signed N·L",
      "Cool / Warm Mapping",
      "Surface Color",
      "Technical Illustration",
    ],

    references: [
      {
        title:
          "A Non-Photorealistic Lighting Model for Automatic Technical Illustration",
        meta: "Gooch, Gooch, Shirley & Cohen · SIGGRAPH 1998",
        url: "https://www-old.cs.utah.edu/gdc/publications/papers/gooch98.pdf",
      },
    ],
  },

  halftone: {
    eyebrow: "NON-PHOTOREALISTIC RENDERING · HALFTONE",
    title: "Halftone Shading",

    description:
      "A screen-space halftone renderer that represents darker regions with progressively larger ink dots. Lighting controls dot coverage while configurable scale, angle, and softness determine the appearance of the printed pattern.",

    implementation: [
      "Computes scene darkness from ambient and diffuse illumination",
      "Generates a repeating dot grid directly from fragment coordinates in screen space",
      "Rotates the halftone grid by a configurable screen angle",
      "Expands dot radius as illumination decreases, allowing dots to merge in deep shadow",
      "Uses fragment derivatives to anti-alias dot boundaries",
      "Composites the configurable ink color over the conventionally lit surface",
    ],

    pipeline: [
      "Lighting",
      "Darkness",
      "Screen-Space Grid",
      "Dot Coverage",
      "Halftone Composite",
    ],
  },

  outline: {
    eyebrow: "NON-PHOTOREALISTIC RENDERING · OUTLINES",
    title: "Illustrative Outlines",

    description:
      "A single-pass illustrative shader that combines view-dependent silhouettes with local normal variation to emphasize both the outer contour and smaller geometric details of a model.",

    implementation: [
      "Detects silhouettes where the surface normal approaches perpendicular to the view direction",
      "Uses smooth thresholding to provide adjustable silhouette width",
      "Uses screen-space normal derivatives to identify rapid local changes in surface orientation",
      "Combines silhouette and detail masks into a single configurable line treatment",
      "Retains a controllable amount of diffuse shading beneath the illustration lines",
      "Runs entirely in the material shader without a separate geometry or post-processing outline pass",
    ],

    pipeline: [
      "Surface Normal",
      "View Facing",
      "Normal Derivatives",
      "Edge Mask",
      "Illustrative Composite",
    ],
  },

  pbr: {
    eyebrow: "REAL-TIME RENDERING · PHYSICALLY BASED",
    title: "Metallic-Roughness PBR",

    description:
      "A custom Cook–Torrance microfacet shader using a metallic-roughness material model. Surface roughness controls the microfacet distribution while metallic determines the balance between dielectric diffuse reflection and conductor-like specular response.",

    implementation: [
      "Uses a GGX normal distribution function for microfacet orientation",
      "Uses Smith masking-shadowing with the Schlick-GGX geometry approximation",
      "Uses Schlick's approximation for the Fresnel reflectance term",
      "Supports scalar or texture-driven metallic and roughness parameters",
      "Suppresses diffuse reflection as metallic approaches one",
      "Supports surface color, normal, ambient-occlusion, and emissive textures through the shared material system",
      "Applies simple Reinhard tone mapping after direct and ambient lighting",
    ],

    pipeline: [
      "Material Parameters",
      "GGX Distribution",
      "Smith Geometry",
      "Schlick Fresnel",
      "Cook–Torrance BRDF",
    ],
  },
};

function ModelKeyboardControls({ renderObjectRef, resetModel }) {
  const { camera, gl } = useThree();

  useEffect(() => {
    const element = gl.domElement;

    function handleKeyDown(event) {
      /*
       * Don't hijack keyboard input
       * from form controls.
       */
      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "r") {
        event.preventDefault();

        resetModel();

        return;
      }

      const movement = 0.12;

      /*
       * Camera-relative screen axes.
       */
      const right = {
        x: camera.matrixWorld.elements[0],

        y: camera.matrixWorld.elements[1],

        z: camera.matrixWorld.elements[2],
      };

      const up = {
        x: camera.matrixWorld.elements[4],

        y: camera.matrixWorld.elements[5],

        z: camera.matrixWorld.elements[6],
      };

      /*
       * Camera forward direction.
       */
      const forward = {
        x: -camera.matrixWorld.elements[8],

        y: -camera.matrixWorld.elements[9],

        z: -camera.matrixWorld.elements[10],
      };

      let direction = null;

      if (event.shiftKey && (key === "w" || key === "arrowup")) {
        direction = forward;
      } else if (event.shiftKey && (key === "s" || key === "arrowdown")) {
        direction = {
          x: -forward.x,
          y: -forward.y,
          z: -forward.z,
        };
      } else if (key === "w" || key === "arrowup") {
        direction = up;
      } else if (key === "s" || key === "arrowdown") {
        direction = {
          x: -up.x,
          y: -up.y,
          z: -up.z,
        };
      } else if (key === "a" || key === "arrowleft") {
        direction = {
          x: -right.x,
          y: -right.y,
          z: -right.z,
        };
      } else if (key === "d" || key === "arrowright") {
        direction = right;
      }

      if (!direction) {
        return;
      }

      event.preventDefault();

      renderObjectRef.current?.move(
        direction.x * movement,
        direction.y * movement,
        direction.z * movement,
      );
    }

    /*
     * Make the canvas keyboard-focusable.
     */
    element.tabIndex = 0;

    element.addEventListener("keydown", handleKeyDown);

    return () => {
      element.removeEventListener("keydown", handleKeyDown);
    };
  }, [camera, gl, renderObjectRef, resetModel]);

  return null;
}

const RENDERING_TECHNIQUES = new Set([
  "hatching",
  "toon",
  "gooch",
  "halftone",
  "outline",
  "pbr",
]);

function parseRenderingRoute(route) {
  return RENDERING_TECHNIQUES.has(route) ? route : "hatching";
}

export default function RenderingDemo({
  Panel,
  Slider,
  Stat,
  route,
  onNavigate,
  onInfoChange,
}) {
  /*
   * Central renderer state.
   */
  const renderer = useRenderingState();

  const renderObjectRef = useRef();

  const rightDragRef = useRef({
    active: false,
    x: 0,
    y: 0,
  });

  const {
    mode,
    setMode,

    technique,
    setTechnique,

    modelId,
    setModelId,

    surface,
    setSurface,

    textureId,
    setTextureId,

    lighting,
    updateLighting,
    updateLightDirection,

    scene,
    updateScene,

    techniques,
    updateTechnique,
    updateTechniqueTexture,
  } = renderer;

  const { hatching, toon, gooch, halftone, outline, pbr } = techniques;

  useEffect(() => {
    const routedTechnique = parseRenderingRoute(route);

    if (routedTechnique !== technique) {
      setTechnique(routedTechnique);
    }
  }, [route, technique, setTechnique]);

  function navigateTechnique(nextTechnique) {
    setTechnique(nextTechnique);
    setMode("render");
    onNavigate?.(nextTechnique);
  }

  /*
   * TAM resource.
   */
  const tam = useMemo(() => generateTAM(), []);

  useEffect(() => {
    return () => {
      disposeTAM(tam);
    };
  }, [tam]);

  useEffect(() => {
    onInfoChange?.(RENDERING_INFO[technique]);
  }, [technique, onInfoChange]);

  /*
   * Current model.
   */
  const model = getModel(modelId);

  /*
   * Create/dispose selected
   * library texture.
   */
  const baseTexture = useMemo(() => {
    const definition = getTextureDefinition(textureId);

    return definition.create();
  }, [textureId]);

  useEffect(() => {
    return () => {
      if (baseTexture) {
        baseTexture.dispose();
      }
    };
  }, [baseTexture]);

  /*
   * Uploaded texture takes priority
   * over the library texture.
   */
  const activeSurface = useMemo(
    () => ({
      ...surface,

      baseTexture: surface.baseTexture ?? baseTexture,
    }),
    [surface, baseTexture],
  );

  function handlePointerDown(event) {
    if (event.button !== 2) {
      return;
    }

    if (scene.autoRotate) {
      updateScene("autoRotate", false);
    }

    event.preventDefault();

    event.currentTarget.querySelector("canvas")?.focus();

    rightDragRef.current = {
      active: true,
      x: event.clientX,
      y: event.clientY,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event) {
    if (!rightDragRef.current.active) {
      return;
    }

    const deltaX = event.clientX - rightDragRef.current.x;

    const deltaY = event.clientY - rightDragRef.current.y;

    rightDragRef.current.x = event.clientX;

    rightDragRef.current.y = event.clientY;

    renderObjectRef.current?.rotate(deltaX * 0.01, deltaY * 0.01);
  }

  function handlePointerUp(event) {
    if (event.button !== 2) {
      return;
    }

    rightDragRef.current.active = false;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function resetModel() {
    renderObjectRef.current?.reset();
  }

  function handleContextMenu(event) {
    event.preventDefault();
  }

  return (
    <div className="reconstruction-workspace">
      <aside className="reconstruction-sidebar">
        {/* <Panel title="RENDERER">

          <div
            className="rendering-mode-selector"
          >

            <button
              type="button"
              className={
                mode === "render"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() =>
                setMode(
                  "render"
                )
              }
            >
              RENDER
            </button>


            <button
              type="button"
              className={
                mode === "atlas"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() =>
                setMode(
                  "atlas"
                )
              }
            >
              TAM ATLAS
            </button>

          </div>

        </Panel> */}

        <Panel title="TECHNIQUE">
          <TechniqueSelector
            technique={technique}
            setTechnique={navigateTechnique}
            setMode={setMode}
          />
        </Panel>

        <Panel title="MODEL">
          <ModelSelector modelId={modelId} setModelId={setModelId} />
        </Panel>

        <Panel title="SURFACE">
          <SurfaceControls
            surface={surface}
            setSurface={setSurface}
            Slider={Slider}

            textureId={textureId}

            setTextureId={(value) => {
              setTextureId(value);

              setSurface((current) => {
                if (current.baseTexture) {
                  current.baseTexture.dispose();
                }

                return {
                  ...current,
                  baseTexture: null,
                };
              });
            }}

            textureOptions={TEXTURE_LIST}
          />
        </Panel>

        <TechniqueControls
          technique={technique}

          Panel={Panel}

          Slider={Slider}

          pbr={{
            ...pbr,

            setMetallic: (value) => updateTechnique("pbr", "metallic", value),

            setMetallicMap: (texture) =>
              updateTechniqueTexture("pbr", "metallicMap", texture),

            setRoughness: (value) => updateTechnique("pbr", "roughness", value),

            setRoughnessMap: (texture) =>
              updateTechniqueTexture("pbr", "roughnessMap", texture),
          }}

          hatching={{
            ...hatching,

            setInkColor: (value) =>
              updateTechnique("hatching", "inkColor", value),

            setHatchScale: (value) =>
              updateTechnique("hatching", "hatchScale", value),
          }}

          toon={{
            ...toon,

            setBands: (value) => updateTechnique("toon", "bands", value),
          }}

          gooch={{
            ...gooch,

            setCoolColor: (value) =>
              updateTechnique("gooch", "coolColor", value),

            setWarmColor: (value) =>
              updateTechnique("gooch", "warmColor", value),

            setCoolMix: (value) => updateTechnique("gooch", "coolMix", value),

            setWarmMix: (value) => updateTechnique("gooch", "warmMix", value),
          }}

          halftone={{
            ...halftone,

            setInkColor: (value) =>
              updateTechnique("halftone", "inkColor", value),

            setDotSize: (value) =>
              updateTechnique("halftone", "dotSize", value),

            setAngle: (value) => updateTechnique("halftone", "angle", value),

            setSoftness: (value) =>
              updateTechnique("halftone", "softness", value),
          }}

          outline={{
            ...outline,

            setLineColor: (value) =>
              updateTechnique("outline", "lineColor", value),

            setSilhouetteWidth: (value) =>
              updateTechnique("outline", "silhouetteWidth", value),

            setDetailStrength: (value) =>
              updateTechnique("outline", "detailStrength", value),

            setShadingStrength: (value) =>
              updateTechnique("outline", "shadingStrength", value),
          }}
        />

        <Panel title="LIGHT" defaultOpen={false}>
          <LightingControls
            Slider={Slider}

            lightColor={lighting.color}

            setLightColor={(value) => updateLighting("color", value)}

            lightIntensity={lighting.intensity}

            setLightIntensity={(value) => updateLighting("intensity", value)}

            ambientIntensity={lighting.ambient}

            setAmbientIntensity={(value) => updateLighting("ambient", value)}

            lightX={lighting.direction[0]}

            setLightX={(value) => updateLightDirection(0, value)}

            lightY={lighting.direction[1]}

            setLightY={(value) => updateLightDirection(1, value)}

            lightZ={lighting.direction[2]}

            setLightZ={(value) => updateLightDirection(2, value)}
          />
        </Panel>

        <Panel title="SCENE" defaultOpen={false}>
          <SceneControls
            autoRotate={scene.autoRotate}

            setAutoRotate={(value) => updateScene("autoRotate", value)}

            resetModel={() => renderObjectRef.current?.reset()}
          />
        </Panel>

        <Panel title="INFO" defaultOpen={false}>
          <RenderInfo
            Stat={Stat}

            technique={technique}

            model={model}

            textureName={
              surface.baseTexture
                ? "Custom"
                : getTextureDefinition(textureId).name
            }

            tam={tam}

            toon={toon}

            gooch={gooch}

            halftone={halftone}

            outline={outline}

            pbr={pbr}
          />
        </Panel>

        {technique === "hatching" && (
          <Panel title="DEBUG" defaultOpen={false}>
            <button
              type="button"
              className={
                mode === "atlas"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => setMode(mode === "atlas" ? "render" : "atlas")}
            >
              {mode === "atlas" ? "BACK TO RENDER" : "VIEW TAM ATLAS"}
            </button>
          </Panel>
        )}
      </aside>

      <div
        className="reconstruction-viewer"

        onPointerDown={handlePointerDown}

        onPointerMove={handlePointerMove}

        onPointerUp={handlePointerUp}

        onPointerCancel={handlePointerUp}

        onContextMenu={handleContextMenu}
      >
        {mode === "render" && (
          <>
            <Canvas
              camera={{
                position: [0, 0, 4.5],
                fov: 45,
              }}

              gl={{
                antialias: true,
              }}
            >
              <color attach="background" args={["#111312"]} />

              <ModelKeyboardControls
                renderObjectRef={renderObjectRef}

                resetModel={resetModel}
              />

              <RenderObject
                ref={renderObjectRef}

                model={model}

                autoRotate={scene.autoRotate}
              >
                <TechniqueRenderer
                  technique={technique}

                  surface={activeSurface}

                  lighting={lighting}

                  tam={tam}

                  hatching={hatching}

                  toon={toon}

                  gooch={gooch}

                  halftone={halftone}

                  outline={outline}

                  pbr={pbr}
                />
              </RenderObject>

              <OrbitControls
                enablePan={false}

                mouseButtons={{
                  LEFT: MOUSE.ROTATE,

                  MIDDLE: MOUSE.DOLLY,

                  RIGHT: null,
                }}

                minDistance={1.5}

                maxDistance={15}
              />
            </Canvas>

            <div className="viewer-overlay viewer-overlay-top">
              {technique === "hatching"
                ? "TONAL ART MAP HATCHING"
                : technique === "toon"
                  ? "TOON SHADING"
                  : technique === "gooch"
                    ? "GOOCH SHADING"
                    : technique === "halftone"
                      ? "HALFTONE SHADING"
                      : technique === "outline"
                        ? "OUTLINE SHADING"
                        : "PHYSICALLY BASED RENDERING"}
            </div>

            <div className="viewer-overlay viewer-overlay-bottom">
              LEFT DRAG: ORBIT · RIGHT DRAG: ROTATE · WASD: MOVE · SHIFT+W/S:
              DEPTH · R: RESET · SCROLL: ZOOM
            </div>
          </>
        )}

        {mode === "atlas" && <TAMAtlas tam={tam} />}
      </div>
    </div>
  );
}
